import { FunctionComponent, useRef } from "react";
import { useTranslation } from "react-i18next";

import { CampaignDeletionResult } from "@signalist/shared-types";

import { Modal } from "@/components";

import { useGetAssignedToCampaignQuery } from "@/pages/Private/redux/list/list.api";
import { useDeleteCampaignMutation } from "@/pages/Private/redux/list/list-campaign.api";

import { describeSaveFailure } from "@/utils/describeSaveFailure";
import { showGlobalToast } from "@/utils/showGlobalToast";
import { useSingleFlight } from "@/utils/useSingleFlight";

/** The API's code while the list's assignment sweep holds the lock: busy, retry. */
const CAMPAIGN_DELETE_BUSY = "CAMPAIGN_DELETE_BUSY";

export interface DeleteCampaignModalProps {
	listId: number;
	campaignUuid: string;
	campaignName: string;
	onClose: () => void;
	/** The campaign is gone; the result says what the delete did to its leads and steps. */
	onDeleted: (result: CampaignDeletionResult) => void;
	/** Above the dialog it is opened from, such as the gear menu's settings. */
	zIndex?: number;
}

const isBusyRefusal = (error: unknown): boolean => {
	const data = (error as { status?: unknown; data?: { code?: unknown } } | null)?.data;

	return (error as { status?: unknown })?.status === 409 && data?.code === CAMPAIGN_DELETE_BUSY;
};

/**
 * The one confirmation for deleting a campaign (PREC-4258), from the Outreach step's trash icon
 * and from the builder's gear menu alike. Before anything is removed it says how many leads are
 * assigned and what happens to them, and afterwards what was done, so the dialog promises what
 * `CampaignDeletionService` does. The count is required: while it loads, or if it cannot be read,
 * the dialog cannot confirm a delete it could not describe.
 *
 * Once confirmed, the dialog stays until the answer is in: Escape, the backdrop and Cancel do
 * nothing while the delete is pending, so the result always reaches the host that opened it.
 *
 * Render it only while it is open: the count is queried for the campaign it was opened on.
 */
export const DeleteCampaignModal: FunctionComponent<DeleteCampaignModalProps> = ({
	listId,
	campaignUuid,
	campaignName,
	onClose,
	onDeleted,
	zIndex,
}) => {
	const { t } = useTranslation();
	const {
		data: assigned,
		isFetching,
		isError,
	} = useGetAssignedToCampaignQuery(
		{ listId, campaignId: campaignUuid },
		{ refetchOnMountOrArgChange: true }
	);
	const [deleteCampaign] = useDeleteCampaignMutation();
	const { run, isRunning } = useSingleFlight();
	// Set synchronously on confirm: a dismiss right after the click lands before `isRunning` renders.
	const pendingRef = useRef(false);

	const count = assigned?.count;
	const countKnown = !isFetching && !isError && typeof count === "number";

	const handleClose = () => {
		if (!pendingRef.current) {
			onClose();
		}
	};

	const reportFailure = (error: unknown) =>
		showGlobalToast(
			isBusyRefusal(error)
				? t("list.campaignBuilder.deleteCampaignBusy")
				: describeSaveFailure(error, t("list.campaignBuilder.deleteCampaignFailed"))
		);

	const handleDelete = () =>
		run(async () => {
			pendingRef.current = true;

			try {
				const result = await deleteCampaign({ id: listId, campaignUuid }).unwrap();

				showGlobalToast(
					t("list.campaignBuilder.deleteCampaignDone", {
						toNew: result.leadsReturnedToNew,
						ended: result.leadsEnded,
						cancelled: result.schedulesCancelled,
					}),
					5000,
					"success"
				);
				onDeleted(result);
			} catch (error) {
				reportFailure(error);
			} finally {
				pendingRef.current = false;
			}
		});

	const renderBody = () => {
		if (isError) {
			return <p>{t("list.campaignBuilder.deleteCampaignCountFailed")}</p>;
		}

		if (!countKnown) {
			return <p>{t("list.campaignBuilder.deleteCampaignCounting")}</p>;
		}

		if (count === 0) {
			return <p>{t("list.campaignBuilder.deleteCampaignNoLeads", { name: campaignName })}</p>;
		}

		return (
			<>
				<p>{t("list.campaignBuilder.deleteCampaignLeads", { count, name: campaignName })}</p>
				<p>{t("list.campaignBuilder.deleteCampaignOutcome")}</p>
			</>
		);
	};

	return (
		<Modal
			disableSave={!countKnown}
			handleClose={handleClose}
			handleSave={handleDelete}
			isLoading={isRunning}
			isOpened={true}
			submitButtonColor="error"
			submitButtonText={t("list.campaignBuilder.deleteCampaignConfirm")}
			title={t("list.campaignBuilder.deleteCampaignTitle")}
			zIndex={zIndex}
		>
			<div className="flex flex-col gap-2 text-sm text-gray-700 mb-4">{renderBody()}</div>
		</Modal>
	);
};
